import React, { useState } from 'react';
import { Droplet, Thermometer, Users, Cable, Truck, Shield, Check, X, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { vanSpecs, whatsIncluded, notIncluded, fleet } from '../data/mock';

const iconMap = {
  droplet: Droplet,
  thermometer: Thermometer,
  users: Users,
  cable: Cable,
  truck: Truck,
  shield: Shield,
};

const VehicleSection = () => {
  const [selectedVan, setSelectedVan] = useState(fleet[0]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % selectedVan.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + selectedVan.images.length) % selectedVan.images.length);
  };

  const selectVan = (van) => {
    setSelectedVan(van);
    setCurrentImageIndex(0);
  };

  return (
    <section id="vehicle" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            WFP Van Hire - Our Fleet
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Choose Your Vehicle
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional window cleaning vans available for hire across Sussex. Both vehicles are fully fitted with hot water system, 650L tank and electric reels.
          </p>
        </div>

        {/* Fleet Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {fleet.map((van) => (
            <button
              key={van.id}
              onClick={() => selectVan(van)}
              data-testid={`van-card-${van.id}`}
              className={`relative text-left rounded-2xl overflow-hidden transition-all duration-300 ${
                selectedVan.id === van.id
                  ? 'ring-4 ring-sky-500 shadow-2xl scale-[1.02]'
                  : 'ring-1 ring-slate-200 hover:ring-sky-300 hover:shadow-lg'
              }`}
            >
              <div className="relative h-48 sm:h-56 overflow-hidden bg-slate-100">
                <img
                  src={van.heroImage}
                  alt={van.name}
                  className="w-full h-full object-cover"
                />
                {selectedVan.id === van.id && (
                  <div className="absolute top-3 right-3 bg-sky-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                    Selected
                  </div>
                )}
                {van.id === 'expert' && selectedVan.id !== van.id && (
                  <div className="absolute top-3 right-3 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                    New
                  </div>
                )}
              </div>
              <div className="p-5 bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    selectedVan.id === van.id ? 'bg-sky-100' : 'bg-slate-100'
                  }`}>
                    <Car className={`w-5 h-5 ${selectedVan.id === van.id ? 'text-sky-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{van.name}</h3>
                    <p className="text-sm text-slate-500">{van.engine}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{van.year}</span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{van.wheelbase}</span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{van.color}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Van Details */}
        <div className="bg-slate-50 rounded-3xl p-6 lg:p-10 mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900">{selectedVan.name}</h3>
              <p className="text-slate-500">{selectedVan.engine} • {selectedVan.year} • {selectedVan.wheelbase}</p>
            </div>
          </div>

          {/* Photo Gallery */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-100 max-h-[500px] flex items-center justify-center">
              <img
                src={selectedVan.images[currentImageIndex].src}
                alt={selectedVan.images[currentImageIndex].alt}
                className="w-full h-auto max-h-[500px] object-contain"
              />
              
              {/* Navigation Arrows */}
              {selectedVan.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
                    data-testid="gallery-prev-btn"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-700" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
                    data-testid="gallery-next-btn"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-700" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImageIndex + 1} / {selectedVan.images.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            {selectedVan.images.length > 1 && (
              <div className="flex justify-center gap-3 mt-4 overflow-x-auto pb-2">
                {selectedVan.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-20 h-14 rounded-lg overflow-hidden transition-all duration-200 flex-shrink-0 ${
                      index === currentImageIndex 
                        ? 'ring-2 ring-sky-600 ring-offset-2 scale-105' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Vehicle Specs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white rounded-xl p-4 text-center">
              <Droplet className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">{selectedVan.specs.tank}</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <Thermometer className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">Hot Water</p>
              <p className="text-xs text-slate-500">Immersion Heater</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <Users className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">{selectedVan.specs.setup}</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <Cable className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">{selectedVan.specs.reels}</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {vanSpecs.features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={index}
                className="group bg-slate-50 hover:bg-sky-50 rounded-2xl p-6 lg:p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-sky-100 group-hover:bg-sky-200 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300">
                  {IconComponent && <IconComponent className="w-7 h-7 text-sky-600" />}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* What's Included / Not Included */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Included */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 lg:p-8">
            <h3 className="text-xl font-bold text-emerald-800 mb-6 flex items-center gap-2">
              <Check className="w-6 h-6" />
              What's Included
            </h3>
            <ul className="space-y-3">
              {whatsIncluded.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-emerald-700">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Not Included */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 lg:p-8">
            <h3 className="text-xl font-bold text-amber-800 mb-6 flex items-center gap-2">
              <X className="w-6 h-6" />
              Not Supplied
            </h3>
            <ul className="space-y-3">
              {notIncluded.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-amber-700">
                  <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VehicleSection;
